import * as React from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import DashboardAlert from "./DashboardAlert/DashboardAlert";
import DashboardMessages from "./DashboardMessages/DashboardMessages";
import DashboardMiscellaneous from "./DashboardMiscellaneous/DashboardMiscellaneous";
import {useAppSelector, store} from "../../../redux/store";
import {useNavigate} from "react-router-dom";
import {dashBoardStyle} from "./dashBoardStyle";
import {getRequest, postRequest, setHeader} from "repository/genericRepository";
import {
  FETCH_DASHBOARD_DATA,
  DASHBOARD_ANNOUNCEMENT_ENDPOINT,
} from "commonUtils/globalAPISettings";
import Skeleton from "@mui/material/Skeleton";
import {isValidObject, loggedMessage, useAlertTimeout} from "commonUtils/utils";
import {
  Alerts,
  AxiosErrorCode,
  CPSMessages,
  HttpStatusCode,
  PageNames,
} from "commonUtils/enumerations";
import {
  HttpException,
  ServiceAuthenticationException,
} from "services/exceptionHandlerService";
import FormValidationAlert from "components/FormComponents/Alert/FormValidationAlert";
import {Loader} from "components/FormComponents/Loader/Loader";
import {dashboardDataInitial, type DashboardProps} from "../dashboardType";

const Dashboard: React.FC = () => {
  const [loader, setLoader] = React.useState<boolean>(false);
  const [dashBoardData, setDashboardData] =
    React.useState<DashboardProps>(dashboardDataInitial);
  const [errorMsg, setErrorMsg] = React.useState<string>("");
  const [announcementdialogopen, setAnnounceDialogOpen] =
    React.useState<boolean>(false);
  const [announcementSuccess, setAnnouncementSuccess] =
    React.useState<string>("");
  const [showAlert, setShowAlert] = React.useState<boolean>(false);

  const {userDetails} = useAppSelector(state => {
    return state.auth;
  });

  const navigate = useNavigate();

  React.useEffect(() => {
    try {
      const authState = store.getState();
      if (!authState?.auth?.authDetails.isAuthenticated) {
        navigate("/");
      }
      const handleContextmenu = (e: {preventDefault: () => void}) => {
        e.preventDefault();
      };
      document.addEventListener("contextmenu", handleContextmenu);
      return function cleanup() {
        document.removeEventListener("contextmenu", handleContextmenu);
      };
    } catch (error: any) {
      handleAxiosErrors(error);
    }
  }, []);

  React.useEffect(() => {
    try {
      if (userDetails?.profileId > 0) {
        getDashboard();
      }
    } catch (error: any) {
      handleAxiosErrors(error);
    }
  }, [userDetails]);

  const deleteMasterMessage = async (id: number) => {
    try {
      setLoader(true);
      const authState = store.getState();
      const APIHeaders = {
        Authorization: `Bearer ${authState.auth?.authDetails?.token}`,
      };
      const announcementKey = id;
      const switchedProfileId = authState.auth?.userDetails?.switchedProfileId;
      const profileID = authState.auth?.userDetails?.profileId;
      const responseData = await postRequest({
        url: DASHBOARD_ANNOUNCEMENT_ENDPOINT,
        headers: setHeader(APIHeaders),
        timeout: 60000,
        params: {announcementKey, switchedProfileId, profileID},
      });
      if (isValidObject(responseData)) {
        setAnnounceDialogOpen(false);
        useAlertTimeout(setShowAlert);
        setAnnouncementSuccess(CPSMessages.ANNOUNCEMENTSUCCESS);
      }
      setLoader(false);
    } catch (error: any) {
      loggedMessage(error, errorMsg, PageNames.DASHBOARD);
      setLoader(false);
      handleAxiosErrors(error);
    }
  };

  const getDashboard = React.useMemo(
    () => async () => {
      try {
        const authState = store.getState();
        const APIHeaders = {
          Authorization: `Bearer ${authState?.auth?.authDetails?.token}`,
        };
        const id = authState?.auth?.userDetails?.profileId;
        const responseData = await getRequest({
          url: FETCH_DASHBOARD_DATA + "?profileId=" + id,
          headers: setHeader(APIHeaders),
          timeout: 120000,
          params: {},
        });
        if (isValidObject(responseData)) {
          setDashboardData(responseData);
        }
      } catch (error: any) {
        loggedMessage(error, errorMsg, PageNames.DASHBOARD);
        handleAxiosErrors(error);
      }
    },
    [dashBoardData],
  );

  const handleAxiosErrors = (error: any) => {
    const data = {pagename: PageNames.DASHBOARD};
    if (error?.code === AxiosErrorCode.ERR_BAD_REQUEST) {
      if (
        error?.response?.status === HttpStatusCode.UNAUTHORIZED ||
        error?.response?.status === HttpStatusCode.FORBIDDEN
      ) {
        ServiceAuthenticationException(error, data, error?.response?.status);
      } else if (error?.response?.status === HttpStatusCode.BAD_REQUEST) {
        setErrorMsg(error.response.data.message);
      } else if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
        setErrorMsg(CPSMessages.SERVICE_NOT_FOUND);
      } else if (
        error?.response?.status === HttpStatusCode.INTERNAL_SERVER_ERROR
      ) {
        HttpException(error, data);
        setErrorMsg(CPSMessages.INTERNAL_SERVER_ERROR);
      } else {
        setErrorMsg(error.message);
      }
    } else if (error?.code === AxiosErrorCode.ECONNABORTED) {
      setErrorMsg(CPSMessages.TIMEOUT_ERROR);
    } else if (error?.code === AxiosErrorCode.ERR_NETWORK) {
      setErrorMsg(CPSMessages.NETWORK_ERROR);
    } else {
      setErrorMsg(error.message);
    }
  };

  return (
    <>
      <Loader open={loader} />
      {errorMsg && (
        <FormValidationAlert severity={Alerts.ERROR} message={errorMsg} />
      )}
      {Object.keys(dashBoardData).length !== 0 ? (
        <>
          <Container maxWidth={false} sx={dashBoardStyle.containerStyle}>
            <Grid container spacing={0}>
              {showAlert && (
                <Grid item xs={12} lg={12}>
                  <FormValidationAlert
                    severity={Alerts.SUCCESS}
                    message={announcementSuccess}
                  />
                </Grid>
              )}
            </Grid>
          </Container>
          <Container maxWidth={false} sx={dashBoardStyle.containerStyle}>
            <Grid container spacing={3}>
              <Grid item xs={12} lg={4}>
                {Object.keys(dashBoardData).length !== 0 && (
                  <DashboardAlert alertsData={dashBoardData?.dashboardAlerts} />
                )}
              </Grid>
              <Grid item xs={12} lg={4}>
                {Object.keys(dashBoardData).length !== 0 && (
                  <DashboardMessages
                    getDashboard={() => {
                      getDashboard();
                    }}
                    deleteMasterMessage={deleteMasterMessage}
                    messagesData={dashBoardData?.announcements}
                    isViewAllAnnouncement={dashBoardData?.isViewAllAnnouncement}
                    announcementdialogopen={announcementdialogopen}
                    setAnnounceDialogOpen={setAnnounceDialogOpen}
                    showAlert={showAlert}
                    deletedMessage={announcementSuccess}
                  />
                )}
              </Grid>
              <Grid item xs={12} lg={4}>
                {Object.keys(dashBoardData).length !== 0 && (
                  <DashboardMiscellaneous miscData={dashBoardData} />
                )}
              </Grid>
            </Grid>
          </Container>
        </>
      ) : (
        <Container maxWidth={false} sx={dashBoardStyle.containerStyle}>
          <Grid container spacing={4} m={"-2rem"} sx={{marginTop: "1rem"}}>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton variant="text" />
            </Grid>
          </Grid>
        </Container>
      )}
    </>
  );
};

export default Dashboard;
